import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import PublicNav from "../components/PublicNav";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Alert, AlertTitle, AlertDescription } from "../components/ui/alert";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { DISCLOSED_ENTITY } from "../lib/entity";
import { SUPPORT_EMAIL } from "../lib/brand";
import { hasHonouredReturnPath, markDisclaimerAcknowledged, returnPathFromSearch } from "../lib/disclaimerGate";
import { AlertTriangle, ShieldCheck, Printer, Check } from "lucide-react";

// STARSEED-CAPMARKETS-257 — the member landing pages route a member here
// before letting them in, with `?next=<where to return>`; `returnPathFromSearch`
// (lib/disclaimerGate.js) honours only a same-origin path. The checklist
// links here with nothing, and those members go back to the checklist as
// before.
export { returnPathFromSearch };

const DisclaimerPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [ackError, setAckError] = useState(null);
  const [loadError, setLoadError] = useState(false);

  // The disclosure text. The acknowledge button is disabled until it has
  // rendered (the receipt records the version the member READ), so a failed
  // load must be visible and retryable — a member sent here by a gate has
  // nowhere else to go.
  const loadDisclosure = async () => {
    setLoadError(false);
    try {
      const r = await api.get("/legal/disclaimer");
      setData(r.data);
    } catch {
      setLoadError(true);
    }
  };

  useEffect(() => {
    loadDisclosure();
  }, []);

  // 257: the gate that sent the member here says so in the URL. The page
  // tells them why they are here and where "Acknowledge" takes them.
  const returnPath = returnPathFromSearch(location.search);
  // Only when the return path is one we will actually honour: the notice
  // promises to send the member back, and a rejected `next` falls back to
  // the checklist instead.
  const sentByGate = hasHonouredReturnPath(location.search);

  const acknowledge = async () => {
    setAckError(null);
    let recorded = false;
    try {
      // Identity comes from the Authorization header (the api client sends
      // it) — the backend binds the receipt to the session and ignores any
      // user_id param (onboarding-gate-003). Logged-out visitors get an
      // analytics no-op (recorded: false).
      const r = await api.post("/legal/disclaimer/acknowledge", null, {
        params: { version: data?.version },
      });
      if (user?.id && r?.data?.recorded === false) {
        // Logged-in per the UI but the backend saw no session — stale local
        // state. Don't show success for a receipt that wasn't stored.
        setAckError(
          "Your session has expired, so the acknowledgement was not recorded. Please log in again and retry."
        );
        return;
      }
      setAcknowledged(true);
      recorded = r?.data?.recorded === true;
    } catch (error) {
      // The POST failed — no compliance receipt was recorded. Do NOT flip to
      // the acknowledged success state (that would tell the user a legal
      // acknowledgement was stored when it was not). Keep the button rendered
      // and retryable, surface an actionable inline error, and log status +
      // server detail only (never the full Axios error — its config carries
      // the request params, which include user_id).
      const serverMessage = error?.response?.data?.detail;
      console.error(
        "DisclaimerPage: disclaimer acknowledgement failed",
        { status: error?.response?.status ?? null, detail: serverMessage ?? error?.message ?? null }
      );
      if (error?.response) {
        // Backend rejected the request (non-2xx) — surface the server's message if any.
        setAckError(
          (typeof serverMessage === "string" && serverMessage) ||
            `We couldn't record your acknowledgement. Please try again, or email us at ${SUPPORT_EMAIL}.`
        );
      } else {
        // No response — network failure (offline, DNS, timeout).
        setAckError(
          `We couldn't reach our servers, so your acknowledgement was not recorded. Please check your connection and try again, or email us at ${SUPPORT_EMAIL}.`
        );
      }
      return;
    }
    if (recorded) {
      // A RECORDED receipt means the backend bound it to a session, so the
      // member is signed in whether or not the auth context has hydrated yet
      // (this page is public; on a hard reload the click can beat /auth/me).
      // Return them to where they came from (forge-20260610-002, 257) rather
      // than stranding them on a public legal page; logged-out readers keep
      // the inline confirmation. Outside the try: a navigation failure is not
      // a recording failure and must not be reported as one.
      markDisclaimerAcknowledged(user?.id);
      toast.success("Disclosure acknowledgement recorded.");
      navigate(returnPath, { replace: true });
    }
  };

  const print = () => window.print();

  return (
    <div className="min-h-screen bg-slate-50 noise-bg" data-testid="disclaimer-page">
      {/* Shared public nav (forge-20260609-003) — replaces the page-local
          white bar (off-brand on the navy site). print:hidden wrapper keeps
          printed disclaimers clean; the Print action moved into the hero. */}
      <div className="print:hidden">
        <PublicNav active="disclaimer" />
      </div>

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-50 via-white to-blue-50/40 border-b border-slate-200 print:bg-white">
        <div className="max-w-5xl mx-auto px-6 py-12 md:py-16">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="text-blue-600" size={16} />
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              Legal disclosure · v{data?.version || "—"}
            </span>
            <Button variant="outline" size="sm" onClick={print} data-testid="print-btn" className="ml-auto print:hidden">
              <Printer size={14} className="mr-1" /> Print
            </Button>
          </div>
          <h1 className="text-3xl md:text-4xl font-serif tracking-tight text-slate-900">
            {data?.title || "Trading Risk Disclaimer & Legal Disclosure"}
          </h1>
          <Alert className="mt-6 border-amber-300 bg-amber-50">
            <AlertTriangle className="text-amber-600" />
            <AlertTitle className="text-slate-900">IMPORTANT</AlertTitle>
            <AlertDescription className="text-slate-700">
              {data?.intro ||
                "By using this website or our services, you explicitly agree to the following terms."}
            </AlertDescription>
          </Alert>
          {/* 257 — a member sent here by a landing-page gate is told why, and
              where "I have read and agree" takes them. Without this the page
              is the unchanged public legal page and the nav's Dashboard
              button bounces them straight back into the gate. */}
          {sentByGate && user?.id && !acknowledged && (
            <Alert className="mt-4 border-blue-300 bg-blue-50 print:hidden" data-testid="disclaimer-gate-notice">
              <ShieldCheck className="text-blue-600" />
              <AlertTitle className="text-slate-900">One step before you continue</AlertTitle>
              <AlertDescription className="text-slate-700">
                Members acknowledge the current version of this disclosure once. Please read it and click
                &ldquo;I have read and agree&rdquo; at the bottom — you will be returned to where you were.
              </AlertDescription>
            </Alert>
          )}
        </div>
      </section>

      {/* Body */}
      <section className="max-w-5xl mx-auto px-6 py-12 print:py-4">
        <Card className="bg-white border-slate-200 shadow-sm print:shadow-none print:border-0">
          <CardContent className="p-8 md:p-12 space-y-10 leading-relaxed text-slate-700">
            {(data?.sections || []).map((s, i) => (
              <Section key={s.heading || `disclaimer-${i}`} idx={i} section={s} />
            ))}

            {loadError && !data && (
              <Alert variant="destructive" className="border-red-300 bg-red-50 print:hidden" data-testid="disclaimer-load-error">
                <AlertTriangle className="text-red-600" />
                <AlertTitle className="text-red-800">The disclosure could not be loaded</AlertTitle>
                <AlertDescription className="text-red-700 flex items-center justify-between gap-4 flex-wrap">
                  <span>We couldn&rsquo;t reach our servers, so there is nothing to acknowledge yet.</span>
                  <Button variant="outline" size="sm" onClick={loadDisclosure} data-testid="disclaimer-load-retry">
                    Try again
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            {/* Acknowledge */}
            <div className="pt-6 border-t border-slate-200 print:hidden">
              {acknowledged ? (
                <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3" data-testid="ack-confirmation">
                  <Check size={18} /> Acknowledgement recorded. Thank you.
                </div>
              ) : (
                <div className="space-y-4">
                  {ackError && (
                    <Alert variant="destructive" className="border-red-300 bg-red-50" data-testid="ack-error">
                      <AlertTriangle className="text-red-600" />
                      <AlertTitle className="text-red-800">Acknowledgement not recorded</AlertTitle>
                      <AlertDescription className="text-red-700">{ackError}</AlertDescription>
                    </Alert>
                  )}
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <p className="text-sm text-slate-600 max-w-xl">
                      By clicking acknowledge, you confirm you have read,
                      understood, and agreed to this full disclaimer.
                    </p>
                    {/* Disabled until the disclosure has actually rendered:
                        the receipt records the version the member READ, and
                        with no `data` the page shows no sections while the
                        backend would record the current version anyway. */}
                    <Button
                      onClick={acknowledge}
                      disabled={!data?.version}
                      className="bg-gold hover:bg-gold-light text-navy-dark shadow-sm"
                      data-testid="ack-btn"
                    >
                      I have read and agree
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <p className="text-xs text-slate-400 text-center mt-6">
          {data?.entity || "EchoSense Capital Club"} · Disclaimer version {data?.version || "—"}
        </p>
      </section>

      <footer className="bg-white border-t border-slate-200 mt-12 print:hidden">
        <div className="max-w-5xl mx-auto px-6 py-6 space-y-3">
          <div className="text-center text-[11px] text-slate-500 leading-relaxed">
            <span className="font-semibold text-slate-700">{DISCLOSED_ENTITY.name}</span>
            <br />
            {DISCLOSED_ENTITY.address}
          </div>
          <div className="flex items-center justify-between flex-wrap gap-2 text-xs text-slate-500">
            <span>© {new Date().getFullYear()} {DISCLOSED_ENTITY.name}. All rights reserved.</span>
            <div className="flex gap-4">
              <Link to="/about" className="hover:text-slate-700">About</Link>
              <Link to="/contact" className="hover:text-slate-700">Contact</Link>
              <Link to="/disclaimer" className="text-blue-600 hover:text-blue-700 font-medium">Disclaimer</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

const Section = ({ idx, section }) => (
  <section data-testid={`disclaimer-section-${idx}`}>
    <h2 className="text-xl font-semibold text-slate-900 mb-3">{section.heading}</h2>
    {(section.body || []).map((p, i) => (
      <p key={`body-${idx}-${i}`} className="mb-3 text-base">{p}</p>
    ))}
    {section.bullets?.length > 0 && (
      <ul className="list-disc list-inside space-y-1.5 mt-2 text-base">
        {section.bullets.map((b, i) => (
          <li key={`bullet-${idx}-${i}`}>{b}</li>
        ))}
      </ul>
    )}
    {section.footer && (
      <p className="mt-3 text-base font-medium text-slate-900">{section.footer}</p>
    )}
  </section>
);

export default DisclaimerPage;
